/**
 * Production-safe logging utility
 * Sanitizes sensitive data and provides structured logging
 */

const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sanitises sensitive data for logging
 * @param {any} data - Data to sanitise
 * @returns {any} Sanitised data safe for logging
 */
const sanitiseData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sensitiveFields = [
    'password', 'token', 'key', 'secret', 'auth', 'authorization',
    'csrf', 'session', 'cookie', 'email', 'phone', 'ssn', 'nin',
  ];

  const sanitised = { ...data };

  Object.keys(sanitised).forEach(key => {
    const keyLower = key.toLowerCase();

    // Check if field contains sensitive information
    if (sensitiveFields.some(field => keyLower.includes(field))) {
      if (typeof sanitised[key] === 'string' && sanitised[key].length > 3) {
        sanitised[key] = sanitised[key].slice(0, 3) + '***';
      } else {
        sanitised[key] = '***';
      }
    }

    // Recursively sanitise nested objects
    if (typeof sanitised[key] === 'object' && sanitised[key] !== null) {
      sanitised[key] = sanitiseData(sanitised[key]);
    }
  });

  return sanitised;
};

/**
 * Creates a timestamp for log entries
 * @returns {string} ISO timestamp
 */
const getTimestamp = () => new Date().toISOString();

/**
 * Formats log message with metadata
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {object} meta - Additional metadata
 * @returns {object} Formatted log entry
 */
const formatLogEntry = (level, message, meta = {}) => ({
  timestamp: getTimestamp(),
  level,
  message,
  meta: isProduction ? sanitiseData(meta) : meta,
  environment: process.env.NODE_ENV,
});

/**
 * Safe logger that prevents sensitive data leakage
 */
export const logger = {
  /**
   * Log error messages
   * @param {string} message - Error message
   * @param {object} data - Additional data to log
   */
  error: (message, data = {}) => {
    const logEntry = formatLogEntry('ERROR', message, data);

    if (isProduction) {
      // In production, only log the message to avoid data exposure
      console.error(`[${logEntry.timestamp}] ERROR: ${message}`);
    } else {
      // In development, log full details
      console.error(`[${logEntry.timestamp}] ERROR: ${message}`, logEntry.meta);
    }
  },

  /**
   * Log warning messages
   * @param {string} message - Warning message
   * @param {object} data - Additional data to log
   */
  warn: (message, data = {}) => {
    const logEntry = formatLogEntry('WARN', message, data);

    if (isProduction) {
      console.warn(`[${logEntry.timestamp}] WARN: ${message}`);
    } else {
      console.warn(`[${logEntry.timestamp}] WARN: ${message}`, logEntry.meta);
    }
  },

  /**
   * Log info messages (development only)
   * @param {string} message - Info message
   * @param {object} data - Additional data to log
   */
  info: (message, data = {}) => {
    if (isDevelopment) {
      const logEntry = formatLogEntry('INFO', message, data);
      console.info(`[${logEntry.timestamp}] INFO: ${message}`, logEntry.meta);
    }
  },

  /**
   * Log debug messages (development only)
   * @param {string} message - Debug message
   * @param {object} data - Additional data to log
   */
  debug: (message, data = {}) => {
    if (isDevelopment) {
      const logEntry = formatLogEntry('DEBUG', message, data);
      console.debug(`[${logEntry.timestamp}] DEBUG: ${message}`, logEntry.meta);
    }
  },

  /**
   * Log API requests safely
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint (sanitised in production)
   * @param {number} status - Response status
   * @param {object} meta - Additional metadata
   */
  apiRequest: (method, endpoint, status, meta = {}) => {
    const safeEndpoint = isProduction ? '[API_ENDPOINT]' : endpoint;
    const message = `${method} ${safeEndpoint} - ${status}`;

    if (status >= 400) {
      logger.error(`API Error: ${message}`, meta);
    } else {
      logger.info(`API Request: ${message}`, meta);
    }
  },

  /**
   * Log validation errors safely
   * @param {string} field - Field name (generic in production)
   * @param {string} message - Validation message
   * @param {any} value - Field value (sanitised)
   */
  validation: (field, message, value = null) => {
    const safeField = isProduction ? '[FIELD]' : field;
    const safeValue = value ? sanitiseData({ value }).value : null;

    logger.warn(`Validation failed for ${safeField}: ${message}`, {
      field: safeField,
      value: safeValue,
    });
  },

  /**
   * Log user actions (with privacy protection)
   * @param {string} action - User action
   * @param {string} userId - User ID (hashed in production)
   * @param {object} meta - Additional metadata
   */
  userAction: (action, userId = null, meta = {}) => {
    const safeUserId = userId && isProduction
      ? `user_${userId.slice(0, 3)}***` : userId;

    logger.info(`User action: ${action}`, {
      userId: safeUserId,
      ...sanitiseData(meta),
    });
  },
};

/**
 * Structured error logging for catch blocks
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {object} meta - Additional metadata
 */
export const logError = (error, context, meta = {}) => {
  logger.error(`${context}: ${error.message}`, {
    stack: isDevelopment ? error.stack : undefined,
    type: error.constructor.name,
    ...meta,
  });
};

/**
 * Performance logging utility
 * @param {string} operation - Operation name
 * @param {number} duration - Duration in milliseconds
 * @param {object} meta - Additional metadata
 */
export const logPerformance = (operation, duration, meta = {}) => {
  if (duration > 1000) {
    logger.warn(`Slow operation: ${operation} took ${duration}ms`, meta);
  } else {
    logger.info(`Performance: ${operation} completed in ${duration}ms`, meta);
  }
};
